/**
 * Типы конструктора отчётов и готовых отчётов (ТЗ §4.6) — тикет #76.
 *
 * Имена полей повторяют Java-записи (`ReportDefinition`, `ReportCatalog.Source`) буква в
 * букву: JSON ходит между ними без перекладывания, и любое расхождение здесь превращается
 * в поле, которое молча приезжает `undefined`.
 */

/** Агрегат над мерой. Список приходит с сервера — он там перечисление, а не строка. */
export type Aggregate = 'sum' | 'avg' | 'min' | 'max' | 'count'

/** Тип колонки витрины: определяет, какие операторы фильтра к ней применимы. */
export type FieldType = 'text' | 'number' | 'date' | 'uuid' | 'boolean'

/** Оператор фильтра. Совпадает с `ReportDefinition.Operator.code()`. */
export type ReportOperator =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'between'
  | 'is_null'
  | 'is_not_null'

/** Колонка витрины. */
export interface ReportField {
  name: string
  type: FieldType
}

/**
 * Витрина, доступная конструктору.
 *
 * Приходит с сервера и только оттуда: белый список витрин и колонок живёт в коде
 * (`ReportCatalog`), а не в таблице и не здесь. Клиент рисует форму по тому, что ему
 * разрешили, и не может расширить список — попытка назвать колонку вне витрины
 * отвергается сервером, а не проверкой на этой стороне.
 */
export interface ReportSource {
  code: string
  resource: string
  dimensions: ReportField[]
  measures: ReportField[]
  filters: ReportField[]
  aggregates: Aggregate[]
  operators: ReportOperator[]
}

/** Мера отчёта: колонка, агрегат над ней и псевдоним колонки результата. */
export interface ReportMeasure {
  field: string
  aggregate: Aggregate
  alias: string
}

/** Условие отбора. Значение уезжает параметром, а не текстом в SQL. */
export interface ReportFilter {
  field: string
  operator: ReportOperator
  value: unknown
}

/** Сортировка результата. */
export interface ReportSort {
  field: string
  descending: boolean
}

/** Декларативное определение отчёта — то, что хранится в `report_definition.definition`. */
export interface ReportDefinition {
  source: string
  dimensions: string[]
  measures: ReportMeasure[]
  filters: ReportFilter[]
  sort: ReportSort[]
  limit: number | null
}

/** Сохранённый отчёт. */
export interface SavedReport {
  id: string
  code: string
  name: string
  description: string | null
  category: ReportCategory
  definition: ReportDefinition
  useReplica: boolean
  isSystem: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/** Категория отчёта; список закрыт схемой (`report_definition_category_chk`). */
export type ReportCategory = 'sales' | 'warehouse' | 'finance' | 'hr'

export const REPORT_CATEGORIES: ReportCategory[] = ['sales', 'warehouse', 'finance', 'hr']

/** Результат прогона: колонки в порядке вывода и строки под ними. */
export interface ReportResult {
  runId: string | null
  reportCode: string
  columns: string[]
  rows: Record<string, unknown>[]
  target: 'primary' | 'replica'
}

/** Строка истории прогонов. */
export interface ReportRun {
  id: string
  requestedBy: string | null
  status: 'queued' | 'running' | 'completed' | 'failed'
  rowCount: number | null
  errorMessage: string | null
  startedAt: string | null
  finishedAt: string | null
}

/** Расписание рассылки. */
export interface ReportSchedule {
  id: string
  reportId: string
  reportCode: string
  reportName: string
  cron: string
  format: 'csv' | 'xlsx' | 'pdf'
  recipientEmails: string[]
  recipientUserIds: string[]
  isActive: boolean
  lastRunAt: string | null
  nextRunAt: string | null
}

/**
 * Письмо рассылки (#139): кому, за какой срок и чем кончилось. Поля без значения сервер не
 * присылает вовсе (`non_null`), поэтому необязательные.
 */
export interface ReportDelivery {
  id: string
  dueAt: string
  recipientUserId?: string | null
  toAddress?: string | null
  format: 'csv' | 'xlsx'
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'rejected'
  attempts: number
  lastError?: string | null
  retryAfter?: string | null
  sentAt?: string | null
  rowCount?: number | null
}

/** Тело запроса на сохранение отчёта. */
export interface SaveReportRequest {
  code: string
  name: string
  description?: string | null
  category: ReportCategory
  definition: ReportDefinition
  useReplica?: boolean
}

/** Тело запроса на заведение расписания. PDF сервер не формирует и не сохраняет (#88). */
export interface SaveScheduleRequest {
  reportId: string
  cron: string
  format: 'csv' | 'xlsx'
  recipientEmails: string[]
  recipientUserIds?: string[]
  isActive?: boolean
}

// ------------------------------------------------------------------ готовые отчёты

/**
 * Конверсия по стадиям воронки.
 *
 * `movedBack` считается отдельно, а не вычитается из `movedOn`: стадия, на которую
 * постоянно возвращаются, — это и есть находка отчёта.
 */
export interface StageConversion {
  stageId: string
  stageName: string
  stageOrder: number
  movedOn: number
  movedBack: number
  won: number
  lost: number
  /** `null`, если стадию никто не проходил: «все застряли» и «никто не доходил» — разное. */
  conversionPercent: number | null
  averageTimeInStage: string | null
}

/** Длительность цикла сделки. */
export interface DealCycle {
  dealCount: number
  averageCycle: string | null
  medianCycle: string | null
  averageDealAmount: string | null
  currency: string | null
}

/** Причина проигрыша с деньгами за ней. */
export interface LossReason {
  reason: string | null
  dealCount: number
  lostAmount: string | null
  currency: string | null
}

/** Разрез отчёта о прибылях и убытках. Перечисление, а не строка. */
export type PnlDimension =
  | 'PRODUCT'
  | 'PRODUCT_CATEGORY'
  | 'EMPLOYEE'
  | 'COUNTERPARTY'
  | 'DEPARTMENT'
  | 'TERRITORY'

export const PNL_DIMENSIONS: PnlDimension[] = [
  'PRODUCT',
  'PRODUCT_CATEGORY',
  'EMPLOYEE',
  'COUNTERPARTY',
  'DEPARTMENT',
  'TERRITORY',
]

/**
 * Строка P&L.
 *
 * `margin` равен `null`, а не нулю, когда себестоимость известна не для всех строк
 * группы: частичная себестоимость завышает маржу, а это направление, которое льстит и
 * потому не проверяется. Сколько строк без неё — в `linesWithoutCost`.
 */
export interface PnlLine {
  dimensionId: string | null
  lineCount: number
  revenue: string | null
  cost: string | null
  margin: string | null
  linesWithoutCost: number
  currency: string | null
  marginPercent: string | null
  complete: boolean
}

/** Отчёт о прибылях и убытках целиком. */
export interface PnlReport {
  dimension: PnlDimension
  from: string
  to: string
  lines: PnlLine[]
  target: 'primary' | 'replica'
  totalRevenue: string
  totalMargin: string
  linesWithoutCost: number
  complete: boolean
}

/** Строка факта ДДС. */
export interface CashFlowLine {
  itemCode: string | null
  itemName: string | null
  direction: string
  amount: string | null
  currency: string | null
  incoming: boolean
}

/**
 * Строка прогноза ДДС.
 *
 * Прогноз — отдельный ряд от факта: свести их в одну колонку значит поставить взвешенную
 * воронку рядом с банковской выпиской. Возвращаются обе суммы — «сколько может прийти» и
 * «на сколько планировать» — потому что это разные вопросы.
 */
export interface CashFlowForecastLine {
  sourceKind: string
  direction: string
  amount: string | null
  weightedAmount: string | null
  currency: string | null
}

/**
 * Дебиторка по срокам.
 *
 * `notYetDue` отделено от корзин просрочки: счёт со сроком через месяц — не долговая
 * проблема, и включать его в просрочку значит показывать её хуже, чем она есть.
 */
export interface AgeingLine {
  counterpartyId: string
  counterpartyName: string | null
  inn: string | null
  outstandingTotal: string | null
  notYetDue: string | null
  bucket0to30: string | null
  bucket31to60: string | null
  bucket61to90: string | null
  bucket90plus: string | null
  currency: string | null
  overdueTotal: string | null
}

/**
 * Канал привлечения: CAC, LTV и их отношение.
 *
 * `acquisitionSpend` и `customerAcquisitionCost` равны `null`, а не нулю, когда затраты
 * не записаны: незаполненная строка — не доказательство, что клиенты достались
 * бесплатно, а нулевой CAC сделал бы канал самым эффективным в отчёте.
 */
export interface UnitEconomicsChannel {
  channel: string | null
  medium: string | null
  customerCount: number
  totalRevenue: string | null
  averageLifetimeValue: string | null
  totalMargin: string | null
  dealCount: number
  dealsWithoutCost: number
  acquisitionSpend: string | null
  currency: string | null
  customerAcquisitionCost: string | null
  ltvToCac: string | null
  marginIsComplete: boolean
}

/** Класс по обороту в деньгах. */
export type AbcClass = 'A' | 'B' | 'C'

/** Класс по стабильности спроса; `UNKNOWN` — истории меньше двух месяцев. */
export type XyzClass = 'X' | 'Y' | 'Z' | 'UNKNOWN'

/** Строка ABC/XYZ-классификации. */
export interface InventoryClassification {
  productId: string
  sku: string | null
  productName: string | null
  totalQuantity: string | null
  totalCost: string | null
  cumulativePercent: string | null
  abcClass: AbcClass
  xyzClass: XyzClass
  variationCoefficient: string | null
  monthsObserved: number
  onHand: string | null
  turnoverRatio: string | null
  combinedClass: string
  needsSafetyStock: boolean
}

/**
 * Строка неликвида.
 *
 * Неликвид и медленный товар — разные находки: не двигавшийся год — это замороженные
 * деньги, а не «низкая оборачиваемость».
 */
export interface DeadStockRow {
  productId: string
  sku: string | null
  productName: string | null
  warehouseId: string | null
  onHand: string | null
  lastMovementAt: string | null
  lastOutboundAt: string | null
  consumedQuantity: string | null
  consumedCost: string | null
  neverShipped: boolean
}

/** Производительность сборщика. */
export interface PickerPerformance {
  employeeId: string
  taskCount: number
  lineCount: number
  pickedQuantity: string | null
  shortLineCount: number
  skippedLineCount: number
  workingSeconds: number
  /** `null` при нулевом времени работы: иначе самым быстрым окажется тот, у кого нет отметок. */
  linesPerHour: string | null
  shortfallPercent: string | null
}

/** Качество поставщика: брак и недопоставка считаются раздельно — это разные претензии. */
export interface SupplierQuality {
  supplierId: string
  supplierName: string | null
  inn: string | null
  receiptCount: number
  receiptsWithDefects: number
  acceptedQuantity: string | null
  rejectedQuantity: string | null
  shortfallQuantity: string | null
  defectPercent: string | null
  receiptsWithDefectsPercent: string | null
}

/** Период отчёта: пара дат в ISO. */
export interface Period {
  from: string
  to: string
}

/**
 * Период по умолчанию — текущий год.
 *
 * Не «последние 30 дней»: у большинства готовых отчётов знаменатель — квартал или год, и
 * месячное окно на юнит-экономике даёт цифры, которые выглядят катастрофой просто из-за
 * длины окна.
 */
export function defaultPeriod(today = new Date()): Period {
  const year = today.getFullYear()
  return { from: `${year}-01-01`, to: iso(today) }
}

/** Дата в ISO без времени, в местной зоне. */
export function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Пустое определение отчёта.
 *
 * `limit` — `null`, а не число: потолок строк задаёт сервер, и поднять его из определения
 * нельзя. Клиент, проставляющий сюда своё число, создавал бы впечатление, что может.
 */
export function emptyDefinition(source: string): ReportDefinition {
  return { source, dimensions: [], measures: [], filters: [], sort: [], limit: null }
}

/**
 * Операторы, применимые к типу колонки.
 *
 * Список сужается по типу, а не предлагается целиком: `contains` над числом даёт
 * ILIKE по числовой колонке — запрос, который либо не выполнится, либо выполнится
 * неожиданно. Окончательное решение всё равно за сервером.
 */
export function operatorsFor(type: FieldType): ReportOperator[] {
  const nullChecks: ReportOperator[] = ['is_null', 'is_not_null']
  switch (type) {
    case 'number':
    case 'date':
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', ...nullChecks]
    case 'text':
      return ['eq', 'ne', 'contains', ...nullChecks]
    case 'uuid':
      return ['eq', 'ne', ...nullChecks]
    case 'boolean':
      return ['eq', ...nullChecks]
  }
}

/**
 * Смена оператора фильтра с приведением формы значения.
 *
 * Диапазон сервер принимает только списком из двух значений, всё остальное — одним
 * значением. Одно поле ввода на «между» отправляло строку, и сервер отвечал отказом
 * «диапазон задаётся двумя значениями» на фильтр, который пользователь заполнил.
 */
export function withOperator(filter: ReportFilter, operator: ReportOperator): ReportFilter {
  const current = filter.value
  if (operator === 'between') {
    const from = Array.isArray(current) ? current[0] : current
    const to = Array.isArray(current) ? current[1] : ''
    return { ...filter, operator, value: [from ?? '', to ?? ''] }
  }
  if (!takesValue(operator)) return { ...filter, operator, value: null }
  return { ...filter, operator, value: Array.isArray(current) ? (current[0] ?? '') : (current ?? '') }
}

/** Нужен ли оператору параметр. */
export function takesValue(operator: ReportOperator): boolean {
  return operator !== 'is_null' && operator !== 'is_not_null'
}

// ------------------------------------------------------------------ дашборды (#77)

/**
 * Тип виджета. Список закрыт схемой (`dashboard_widget_type_chk`).
 *
 * Нарисованы не все, и это названо вслух, а не спрятано: `kpi`, `table` и `funnel`
 * рисуются разметкой, `bar` и `line` — инлайновым SVG. `pie`, `gauge` и `heatmap`
 * рисуются как таблица с пометкой: показать их нечем без библиотеки графиков, а
 * подсунуть вместо них другую картинку значит соврать о том, что видит зритель.
 */
export type WidgetType = 'kpi' | 'line' | 'bar' | 'pie' | 'table' | 'funnel' | 'gauge' | 'heatmap'

export const WIDGET_TYPES: WidgetType[] = [
  'kpi',
  'table',
  'bar',
  'line',
  'funnel',
  'pie',
  'gauge',
  'heatmap',
]

/** Сетка раскладки: двенадцать колонок, как на сервере (`DashboardService.GRID_COLUMNS`). */
export const GRID_COLUMNS = 12

/**
 * Нижняя граница интервала автообновления, в секундах.
 *
 * Повторяет серверную (`DashboardService.MINIMUM_REFRESH_SECONDS`) — и это удобство, а
 * не защита: решает сервер, отказом `dashboard.refresh_too_frequent`. Здесь она нужна,
 * чтобы форма не предлагала значение, которое всё равно отвергнут.
 */
export const MINIMUM_REFRESH_SECONDS = 60

/** Дашборд в списке. */
export interface Dashboard {
  id: string
  name: string
  ownerId: string | null
  isShared: boolean
  visibleToRoleIds: string[]
  isDefault: boolean
  widgetCount: number
  createdAt: string
  updatedAt: string
}

/** Виджет: что показывать, где и как часто обновлять. */
export interface DashboardWidget {
  id: string
  dashboardId: string
  title: string
  widgetType: WidgetType
  /** Идентификатор сохранённого отчёта (#76). */
  dataSource: string
  reportName: string | null
  reportCode: string | null
  parameters: Record<string, unknown>
  gridX: number
  gridY: number
  gridWidth: number
  gridHeight: number
  refreshSeconds: number
}

/**
 * Виджет вместе с тем, что в нём показать.
 *
 * `columns` и `rows` равны `null` при отказе, а не пустым спискам: пустая таблица
 * читается как «данных нет», то есть как штатное состояние, и запрет остался бы
 * невидимым. `errorCode` — машинный код (`report.forbidden_source`), по которому экран
 * решает, показать «нет доступа» или «что-то сломалось».
 */
export interface LoadedWidget {
  widget: DashboardWidget
  columns: string[] | null
  rows: Record<string, unknown>[] | null
  errorCode: string | null
  errorMessage: string | null
}

/** Дашборд с виджетами. */
export interface LoadedDashboard {
  dashboard: Dashboard
  widgets: LoadedWidget[]
}

/** Тело запроса на сохранение дашборда. */
export interface SaveDashboardRequest {
  name: string
  isShared: boolean
  visibleToRoleIds?: string[]
  isDefault?: boolean
}

/** Тело запроса на добавление или правку виджета. */
export interface SaveWidgetRequest {
  title: string
  widgetType: WidgetType
  dataSource: string
  parameters?: Record<string, unknown>
  gridX?: number
  gridY?: number
  gridWidth?: number
  gridHeight?: number
  refreshSeconds?: number
}

/** Новое место виджета на сетке. */
export interface WidgetPlacement {
  widgetId: string
  gridX: number
  gridY: number
  gridWidth: number
  gridHeight: number
}

/**
 * Первое числовое значение результата — то, что показывает виджет-число.
 *
 * `null`, а не ноль, когда числа нет: у отчётов этого проекта `null` значим (маржа без
 * себестоимости, CAC без затрат), и ноль на его месте — утверждение, которого никто не
 * делал.
 */
export function firstNumber(
  columns: string[] | null,
  rows: Record<string, unknown>[] | null,
): { value: number | null; column: string | null } {
  const first = rows?.[0]
  if (!columns || !first) return { value: null, column: null }

  for (const column of columns) {
    const raw = first[column]
    if (raw === null || raw === undefined || raw === '') continue
    const numeric = Number(raw)
    if (Number.isFinite(numeric)) return { value: numeric, column }
  }
  return { value: null, column: null }
}

/**
 * Пары «подпись → величина» для столбчатых и воронковых виджетов.
 *
 * Подпись — первая нечисловая колонка, величина — первая числовая: угадывание, и оно
 * названо вслух. Точная привязка колонок к осям — это настройка виджета, которой здесь
 * пока нет; выбранная колонка показывается на экране, чтобы догадка была видна зрителю,
 * а не молча определяла картинку.
 */
export function seriesOf(
  columns: string[] | null,
  rows: Record<string, unknown>[] | null,
): { label: string; value: number }[] {
  if (!columns || !rows) return []

  const valueColumn = columns.find((column) =>
    rows.some((row) => Number.isFinite(Number(row[column])) && row[column] !== null && row[column] !== ''),
  )
  if (!valueColumn) return []
  const labelColumn = columns.find((column) => column !== valueColumn) ?? valueColumn

  return rows
    .map((row) => ({
      label: String(row[labelColumn] ?? '—'),
      value: Number(row[valueColumn]),
    }))
    .filter((point) => Number.isFinite(point.value))
}
